import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { apiGet, apiPost } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { RefreshCw, Sparkles, AlertCircle, TrendingUp, Wand2, ArrowRight, Image as ImageIcon, FileText, Film, Type } from "lucide-react";

import { OWLBERT_DARK } from "@/lib/owlbert";
const OWL_PORTRAIT = OWLBERT_DARK;

// Owlbert — the insider strategist
export default function OwlbertCard({ workspaceId, workspaceName }) {
  const [brief, setBrief] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [tab, setTab] = useState("ideas"); // ideas | gaps | optimize
  const navigate = useNavigate();

  const load = async (fresh = false) => {
    if (fresh) setRefreshing(true); else setLoading(true);
    try {
      const r = await apiGet(`/workspaces/${workspaceId}/owlbert/brief`, { days: 30, fresh });
      setBrief(r);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Owlbert is sleeping");
    } finally {
      setLoading(false); setRefreshing(false);
    }
  };

  useEffect(() => {
    load(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [workspaceId]);

  const turnIntoDraft = async (idea) => {
    // Deep-link to Compose with the idea pre-filled
    const params = new URLSearchParams({
      topic: `${idea.hook}. ${idea.angle || ""}`.trim(),
      format: (idea.format || "text").includes("carousel") ? "carousel-pdf" : (idea.format || "text").includes("image") ? "single-image" : (idea.format || "text").includes("video") ? "video" : "text",
    });
    navigate(`/workspace/${workspaceId}/compose?${params.toString()}`);
  };

  return (
    <section
      data-testid={`owlbert-card-${workspaceId}`}
      className="surface overflow-hidden fade-up"
    >
      <header className="px-6 pt-5 pb-4 border-b border-hairline flex items-start justify-between gap-3">
        <div className="flex items-start gap-4 min-w-0">
          <img
            src={OWL_PORTRAIT}
            alt="Owlbert"
            className="owlbert-portrait w-12 h-12 object-cover shrink-0"
          />
          <div className="min-w-0">
            <div className="serif text-xl text-parchment leading-tight flex items-center gap-2">
              Owlbert
              <span className="overline" style={{ fontSize: 9, color: "hsl(var(--accent))" }}>
                insider · {workspaceName}
              </span>
            </div>
            {brief?.summary_line && (
              <p className="owlbert-voice mt-2 max-w-2xl" style={{ fontSize: 16 }}>
                {brief.summary_line}
              </p>
            )}
            {!brief?.summary_line && !loading && (
              <p className="text-xs text-stone mt-2">no brief yet. tap refresh.</p>
            )}
          </div>
        </div>
        <button
          onClick={() => load(true)}
          disabled={refreshing}
          data-testid={`owlbert-refresh-${workspaceId}`}
          className="btn-ghost text-xs shrink-0 inline-flex items-center gap-1.5"
        >
          <RefreshCw className={`w-3.5 h-3.5 ${refreshing ? "animate-spin" : ""}`} />
          {refreshing ? "thinking…" : "refresh"}
        </button>
      </header>

      {loading ? (
        <div className="px-5 py-6 overline">owlbert is reading your data…</div>
      ) : !brief || brief.error ? (
        <div className="px-5 py-6 text-sm text-zinc-400 flex items-center gap-2">
          <AlertCircle className="w-4 h-4 text-amber-400" />
          {brief?.error ? "the parser tripped. hit refresh." : "tap refresh to wake owlbert."}
        </div>
      ) : (
        <>
          {/* Tab nav */}
          <div className="px-5 pt-3 flex items-center gap-1 border-b border-zinc-800/60">
            {[
              { k: "ideas", label: `ideas · ${brief.ideas?.length || 0}` },
              { k: "gaps", label: `gaps · ${brief.gaps?.length || 0}` },
              { k: "optimize", label: `optimize · ${brief.optimizations?.length || 0}` },
            ].map((t) => (
              <button
                key={t.k}
                onClick={() => setTab(t.k)}
                data-testid={`owlbert-tab-${t.k}-${workspaceId}`}
                className={`text-xs px-3 py-1.5 mb-[-1px] border-b-2 transition-colors ${
                  tab === t.k
                    ? "border-amber-400 text-zinc-100"
                    : "border-transparent text-zinc-500 hover:text-zinc-300"
                }`}
              >
                {t.label}
              </button>
            ))}
          </div>

          <div className="px-5 py-4">
            {tab === "ideas" && (
              <div className="space-y-3" data-testid={`owlbert-ideas-${workspaceId}`}>
                {(brief.ideas || []).map((idea, i) => (
                  <IdeaRow key={i} idea={idea} onUse={turnIntoDraft} testId={`idea-${i}`} />
                ))}
                {(brief.ideas || []).length === 0 && (
                  <div className="text-xs text-zinc-500">no ideas in this brief.</div>
                )}
              </div>
            )}

            {tab === "gaps" && (
              <ul className="space-y-3" data-testid={`owlbert-gaps-${workspaceId}`}>
                {(brief.gaps || []).map((g, i) => (
                  <li key={i} className="border-l-2 border-rose-500/40 pl-3" data-testid={`gap-${i}`}>
                    <div className="text-sm text-zinc-200">{g.observation}</div>
                    <div className="text-xs text-zinc-500 mt-1">↳ {g.data_point}</div>
                  </li>
                ))}
                {(brief.gaps || []).length === 0 && <li className="text-xs text-zinc-500">no gaps detected.</li>}
              </ul>
            )}

            {tab === "optimize" && (
              <ul className="space-y-3" data-testid={`owlbert-optimize-${workspaceId}`}>
                {(brief.optimizations || []).map((o, i) => (
                  <li key={i} className="border-l-2 border-sky-500/40 pl-3" data-testid={`opt-${i}`}>
                    <div className="text-sm text-zinc-200">{o.observation}</div>
                    <div className="text-xs text-sky-300 mt-1">→ {o.suggested_change}</div>
                  </li>
                ))}
                {(brief.optimizations || []).length === 0 && <li className="text-xs text-zinc-500">no optimizations yet.</li>}
              </ul>
            )}
          </div>

          <footer className="px-5 py-2.5 border-t border-zinc-800/60 text-xs text-zinc-500 flex items-center justify-between flex-wrap gap-2">
            <span>
              read: {brief.context_stats?.my_posts || 0} of your posts ·
              {" "}{brief.context_stats?.comments || 0} comments ·
              {" "}{brief.context_stats?.network_posts || 0} network posts
            </span>
            <span className="text-zinc-600">{brief.day}</span>
          </footer>
        </>
      )}
    </section>
  );
}

function IdeaRow({ idea, onUse, testId }) {
  const formatIcon = (f) => {
    const v = (f || "").toLowerCase();
    if (v.includes("carousel") || v.includes("pdf")) return <FileText className="w-3 h-3" />;
    if (v.includes("image")) return <ImageIcon className="w-3 h-3" />;
    if (v.includes("video")) return <Film className="w-3 h-3" />;
    return <Type className="w-3 h-3" />;
  };
  return (
    <div
      data-testid={testId}
      className="bg-zinc-950/40 border border-zinc-800/60 rounded-md p-3"
    >
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0 flex-1">
          <div className="text-sm text-zinc-100 mb-1.5">{idea.hook}</div>
          {idea.angle && <div className="text-xs text-zinc-400 mb-2 leading-relaxed">{idea.angle}</div>}
          <div className="flex items-center gap-3 flex-wrap text-xs text-zinc-500">
            <span className="inline-flex items-center gap-1 px-1.5 py-0.5 bg-zinc-900 border border-zinc-800 rounded-sm">
              {formatIcon(idea.format)} {idea.format}
            </span>
            {idea.visual_brief && (
              <span className="text-zinc-400">
                <Sparkles className="w-3 h-3 inline mr-1 text-amber-400" />
                {idea.visual_brief}
              </span>
            )}
          </div>
        </div>
        <button
          onClick={() => onUse(idea)}
          data-testid={`use-idea-${testId}`}
          className="btn-ghost text-xs shrink-0 text-emerald-400"
          title="Turn into a draft in Content Pipeline"
        >
          <Wand2 className="w-3.5 h-3.5 mr-1 inline" />
          use
          <ArrowRight className="w-3 h-3 inline ml-1" />
        </button>
      </div>
    </div>
  );
}
